import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BuildingIcon, ShieldCheckIcon, MapPinIcon, PaletteIcon, LayoutGridIcon, SparklesIcon, UsersIcon, CreditCardIcon, BellIcon, ShieldAlertIcon, PlugIcon, ChevronRightIcon, ChevronLeftIcon, CheckIcon, MonitorIcon, SmartphoneIcon, ExternalLinkIcon, EyeIcon, XIcon, RocketIcon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { useShopStudio } from '../contexts/ShopStudioContext';
import { ShopConfig } from '../data/shopConfig';
import { StorefrontViewport } from './settings/StorefrontViewport';
import { ShopStudio, StudioTab } from './settings/ShopStudio';
import { BusinessDetails } from './settings/sections/BusinessDetails';
import { Verification } from './settings/sections/Verification';
import { LocationsContact } from './settings/sections/LocationsContact';
import { ShopDesign } from './settings/sections/ShopDesign';
import { ShopContent } from './settings/sections/ShopContent';
import { AiExperience } from './settings/sections/AiExperience';
import { ReviewPublish } from './settings/sections/ReviewPublish';
import { IntegrationsApi } from './settings/sections/IntegrationsApi';
import { TeamMembers, PaymentsCarts, NotificationsSection, PlanBilling, DangerZone } from './settings/sections/AccountSections';
type SectionId = 'business' | 'verification' | 'locations' | 'design' | 'content' | 'ai' | 'review' | 'team' | 'payments' | 'integrations' | 'notifications' | 'billing' | 'danger';
interface SectionMeta {
  id: SectionId;
  label: string;
  desc: string;
  icon: React.ElementType;
  /** Edited inside Shop Studio, so these never get their own Settings card. */
  studio?: boolean;
}
const SECTIONS: SectionMeta[] = [{
  id: 'business',
  label: 'Business Details',
  desc: 'Name, logo, shop address and description',
  icon: BuildingIcon
}, {
  id: 'verification',
  label: 'Verification',
  desc: 'Identity, trade licence and review',
  icon: ShieldCheckIcon
}, {
  id: 'locations',
  label: 'Locations & Contact',
  desc: 'Countries, branches and contact methods',
  icon: MapPinIcon
}, {
  id: 'design',
  label: 'Shop Design',
  desc: 'Colours, branding, buttons and appearance',
  icon: PaletteIcon,
  studio: true
}, {
  id: 'content',
  label: 'Shop Content',
  desc: 'Questions, highlights, categories and products',
  icon: LayoutGridIcon,
  studio: true
}, {
  id: 'ai',
  label: 'AI Experience',
  desc: 'Greeting, tone and what your AI can help with',
  icon: SparklesIcon,
  studio: true
}, {
  id: 'team',
  label: 'Team Members',
  desc: 'Members and permissions',
  icon: UsersIcon
}, {
  id: 'payments',
  label: 'Payments & Carts',
  desc: 'Payment methods and checkout',
  icon: CreditCardIcon
}, {
  id: 'integrations',
  label: 'Integrations & API',
  desc: 'Connect your ordering, ERP or dispatch system',
  icon: PlugIcon
}, {
  id: 'notifications',
  label: 'Notifications',
  desc: 'Communication preferences',
  icon: BellIcon
}, {
  id: 'billing',
  label: 'Plan & Billing',
  desc: 'Subscription and listing capacity',
  icon: CreditCardIcon
}, {
  id: 'danger',
  label: 'Danger Zone',
  desc: 'Deactivate or delete your hub',
  icon: ShieldAlertIcon
}];
/** The guided path a brand-new merchant walks through. */
const ONBOARDING: {
  id: SectionId;
  title: string;
}[] = [{
  id: 'business',
  title: 'Tell us about your business'
}, {
  id: 'verification',
  title: 'Verify your business'
}, {
  id: 'locations',
  title: 'Add contact & locations'
}, {
  id: 'design',
  title: 'Brand your shop'
}, {
  id: 'ai',
  title: 'Personalize your AI'
}, {
  id: 'review',
  title: 'Preview & Publish'
}];
/**
 * Business, account and team settings. Everything customer-facing — shop
 * design, content, the AI experience and publishing — lives in the Storefront
 * Studio section instead, so there is exactly one place to manage it.
 */
export function BusinessSettings() {
  const {
    hub
  } = useBusiness();
  const {
    config,
    update,
    undo,
    redo,
    canUndo,
    canRedo,
    resetTheme,
    toast
  } = useShopStudio();
  const [onboarding, setOnboarding] = useState(Boolean(hub?.isNew));
  const [step, setStep] = useState(1);
  const [active, setActive] = useState<SectionId | null>(null);
  const [fullPreview, setFullPreview] = useState(false);
  const currentSection: SectionId | null = onboarding ? ONBOARDING[step - 1].id : active;
  const meta = SECTIONS.find(s => s.id === currentSection);
  // These three are edited in the Shop Studio, where the shop fills the
  // workspace and only the controls panel scrolls.
  const studioTab: StudioTab | null = currentSection === 'design' ? 'design' : currentSection === 'content' ? 'content' : currentSection === 'ai' ? 'ai' : null;
  const body = (() => {
    switch (currentSection) {
      case 'business':
        return <BusinessDetails config={config} update={update} data-id="element-830" data-mp="1ou4e9t1xe1ab" />;
      case 'verification':
        return <Verification config={config} update={update} data-id="element-831" data-mp="1jv9w8tbs8gr9" />;
      case 'locations':
        return <LocationsContact config={config} update={update} data-id="element-832" data-mp="1jtgsblsk0amy" />;
      case 'design':
        return <ShopDesign config={config} update={update} canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} onResetTheme={resetTheme} data-id="element-833" data-mp="w3cq9iv0kiy2" />;
      case 'content':
        return <ShopContent config={config} update={update} data-id="element-834" data-mp="113h6d7s0fbnr" />;
      case 'ai':
        return <AiExperience config={config} update={update} data-id="element-835" data-mp="ddwj4cotps8o" />;
      case 'review':
        return <ReviewPublish config={config} update={update} onOpenPreview={() => setFullPreview(true)} data-id="element-836" data-mp="8fbiwd9kn8k8" />;
      case 'team':
        return <TeamMembers data-id="element-837" data-mp="2dgno6tmoq2xy" />;
      case 'payments':
        return <PaymentsCarts data-id="element-838" data-mp="2df47x3113rw6" />;
      case 'integrations':
        return <IntegrationsApi data-id="element-839" data-mp="2i9gtpjpz9fb3" />;
      case 'notifications':
        return <NotificationsSection data-id="element-840" data-mp="2m9iyi1r0eoul" />;
      case 'billing':
        return <PlanBilling data-id="element-841" data-mp="2m8zd2b25hibt" />;
      case 'danger':
        return <DangerZone data-id="element-842" data-mp="1pk1gqoer5ccj" />;
      default:
        return null;
    }
  })();
  // -------------------------------------------------------------- Shop Studio
  if (studioTab) {
    return <ShopStudio config={config} update={update} initialTab={studioTab} canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} onResetTheme={resetTheme} onboarding={onboarding} step={step} totalSteps={ONBOARDING.length} onBack={() => setStep(s => Math.max(1, s - 1))} onNext={() => setStep(s => Math.min(ONBOARDING.length, s + 1))} onClose={() => {
      if (onboarding) {
        setOnboarding(false);
        setActive(null);
        toast('Saved for later');
      } else {
        setActive(null);
      }
    }} onSave={() => {
      // Removing Talab branding is previewable on any plan, but only the
      // Scale plan can save it.
      if (!config.showTalabBranding && currentPlanId !== 'Scale') {
        update({
          showTalabBranding: true
        });
        toast('Upgrade to Scale to save a header without Talab branding');
        return;
      }
      toast('Changes saved');
    }} data-id="element-843" data-mp="1yk6hifpfxchq" />;
  }
  // ---------------------------------------------------------------- Home cards
  if (!onboarding && !active) {
    return <div className="w-full max-w-6xl mx-auto pb-24" data-id="element-844" data-mp="ulsyeswj4mee">
        <div className="mb-5" data-id="element-845" data-mp="qk7ptjr12ua0">
          <p className="text-sm text-slate-500" data-id="element-846" data-mp="qm2cp3lv0wgq">
            Your business details, team and account. Your shop and widget are
            designed and published in My Shop.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-847" data-mp="1dtswuk1cx03m">
          {SECTIONS.filter(s => s.id !== 'danger' && !s.studio).map(s => {
          const Icon = s.icon;
          const status = s.id === 'verification' ? config.verification.identity === 'verified' ? 'Identity verified' : 'Action needed' : s.id === 'locations' ? `${config.locations.length} location${config.locations.length === 1 ? '' : 's'}` : 'Last updated today';
          return <button key={s.id} onClick={() => setActive(s.id)} className="group text-left p-5 bg-white border border-slate-200 rounded-2xl hover:border-primary-indigo/40 hover:shadow-[0_10px_28px_rgba(79,70,229,0.07)] transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-848" data-mp="m4pr0zttnhov">
                <div className="flex items-start justify-between gap-3" data-id="element-849" data-mp="2lmrrzlenqca9">
                  <span className="w-10 h-10 rounded-xl bg-primary-indigo/[0.06] flex items-center justify-center flex-shrink-0" data-id="element-850" data-mp="2loma6ryjnqan">
                    <Icon className="w-[18px] h-[18px] text-primary-indigo" strokeWidth={1.75} data-id="element-851" data-mp="2unpx670u0puf" />
                  </span>
                  <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-852" data-mp="39e6y7p9k1dqs" />
                </div>
                <p className="font-bold text-dark-navy text-sm mt-3.5" data-id="element-853" data-mp="3icikxg7avrdy">
                  {s.label}
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-snug" data-id="element-854" data-mp="3e9cfubvxc692">
                  {s.desc}
                </p>
                <p className="text-[11px] font-semibold text-slate-400 mt-3 pt-3 border-t border-slate-100 truncate" data-id="element-855" data-mp="1jxfn8flqwucf">
                  {status}
                </p>
              </button>;
        })}
        </div>

        <button onClick={() => setActive('danger')} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-856" data-mp="30lfbi6jjheai">
          <ShieldAlertIcon className="w-4 h-4" data-id="element-857" data-mp="2qosq9gnawl7y" />
          Danger Zone
        </button>

        {fullPreview && <FullPreview config={config} onClose={() => setFullPreview(false)} data-id="element-858" data-mp="3oagc57m4rl58" />}
      </div>;
  }
  // ------------------------------------------------------------ Focused screen
  return <div className="w-full max-w-[1500px] mx-auto pb-28" data-id="element-859" data-mp="39md4zuwcq3eu">
      {/* Onboarding progress */}
      {onboarding ? <div className="mb-6" data-id="element-860" data-mp="awv6u7mgb3o7">
          <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-861" data-mp="ax4wfu3l38td">
            <div data-id="element-862" data-mp="65d2d9l7ykyh">
              <h1 className="font-display font-bold text-2xl lg:text-3xl text-dark-navy tracking-tight" data-id="element-863" data-mp="64v09wbvlcin">
                Let's get your shop ready
              </h1>
              <p className="text-sm text-slate-500 mt-1.5" data-id="element-864" data-mp="ifchwi9z6qja">
                Step {step} of {ONBOARDING.length} ·{' '}
                {Math.round(step / ONBOARDING.length * 100)}% ·{' '}
                <span className="text-slate-400" data-id="element-865" data-mp="sdsbcrwh6bvy">
                  Don't worry — you can change anything later.
                </span>
              </p>
            </div>
            <button onClick={() => {
          setOnboarding(false);
          setActive(null);
          toast('Saved for later');
        }} className="text-sm font-semibold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-866" data-mp="wdkntfozp26l">
              Save for later
            </button>
          </div>

          <div className="flex items-center gap-1.5 mt-4" data-id="element-867" data-mp="1g5h8clk8roj8">
            {ONBOARDING.map((s, i) => <button key={s.id} onClick={() => setStep(i + 1)} title={s.title} aria-label={s.title} className={`h-1.5 flex-1 rounded-full transition-colors duration-150 ease-out ${i + 1 <= step ? 'bg-primary-indigo' : 'bg-slate-200 hover:bg-slate-300'}`} data-id="element-868" data-mp="1k5hkfzjvbnn4" data-mpi={i} />)}
          </div>
          <div className="hidden lg:flex items-center justify-between mt-2.5" data-id="element-869" data-mp="3l6nww5nysdb9">
            {ONBOARDING.map((s, i) => <span key={s.id} className={`text-[11px] font-semibold ${i + 1 === step ? 'text-primary-indigo' : i + 1 < step ? 'text-slate-500' : 'text-slate-300'}`} data-id="element-870" data-mp="3p4qipfegayj" data-mpi={i}>
                {i + 1 < step && <CheckIcon className="w-3 h-3 inline-block mr-1 -mt-0.5" data-id="element-871" data-mp="8psnrsu81o17" />}
                {s.title}
              </span>)}
          </div>
        </div> : <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 mb-5 text-sm font-semibold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-872" data-mp="2ahxa8osybt4j">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-873" data-mp="24dsjbkevayi0" />
          All settings
        </button>}

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]" data-id="element-874" data-mp="1qmw36t24o9ru">
        {/* Section nav for everyday editing */}
        {!onboarding && <nav className="hidden lg:block" aria-label="Settings sections" data-id="element-875" data-mp="1gprfupwjq89g">
            <div className="sticky top-6 space-y-0.5" data-id="element-876" data-mp="1gruchss7ezn5">
              {SECTIONS.filter(s => !s.studio).map(s => {
            const Icon = s.icon;
            const isActive = s.id === currentSection;
            return <button key={s.id} onClick={() => setActive(s.id)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition-colors duration-150 ease-out ${isActive ? 'bg-primary-indigo/[0.07] text-primary-indigo' : s.id === 'danger' ? 'text-slate-400 hover:text-error hover:bg-error/[0.04]' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-877" data-mp="1cn4jcboxjz5x">
                    <Icon className="w-4 h-4 flex-shrink-0" data-id="element-878" data-mp="syv65q5jn660" />
                    <span className="truncate" data-id="element-879" data-mp="o0jtl7nv6hk3">{s.label}</span>
                  </button>;
          })}
            </div>
          </nav>}

        <AnimatePresence mode="wait" data-id="element-880" data-mp="j8sdxs68xw8q">
          <motion.div key={currentSection} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} className="min-w-0" data-id="element-881" data-mp="eaqtb2t69ov5">
            {body}
          </motion.div>
        </AnimatePresence>

      </div>

      {/* Step controls */}
      {onboarding && <div className="sticky bottom-0 mt-8 -mx-4 lg:mx-0 px-4 lg:px-6 py-4 bg-white/90 backdrop-blur-md border-t border-slate-200 flex items-center justify-between gap-3 lg:rounded-2xl lg:border" data-id="element-882" data-mp="2gxfekgvebe33">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-slate-300 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-883" data-mp="2py3vbjc6bsjo">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-884" data-mp="227gf8as3twm1" />
            Back
          </button>
          <button onClick={() => {
        if (step === ONBOARDING.length) {
          setOnboarding(false);
          setActive(null);
          toast('Shop setup saved');
        } else {
          setStep(s => s + 1);
          toast('Saved');
        }
      }} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-885" data-mp="2712jbek7cs68">
            {step === ONBOARDING.length ? 'Finish setup' : 'Save & Continue'}
            <ChevronRightIcon className="w-4 h-4" data-id="element-886" data-mp="2mlmdgi8yyfm6" />
          </button>
        </div>}

      {!onboarding && meta && <div className="mt-8 flex items-center justify-end gap-3" data-id="element-887" data-mp="1tw5ef1qug0gz">
          <button onClick={() => setActive(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:border-slate-300 transition-colors duration-150 ease-out" data-id="element-888" data-mp="1ty00ijdupymn">
            Done
          </button>
          <button onClick={() => toast('Changes saved')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-889" data-mp="3j9im90y606mu">
            <CheckIcon className="w-4 h-4" data-id="element-890" data-mp="3quuw685g9guv" />
            Save Changes
          </button>
        </div>}

      {fullPreview && <FullPreview config={config} onClose={() => setFullPreview(false)} data-id="element-891" data-mp="3cwei1w59co2p" />}
    </div>;
}
function FullPreview({
  config,
  onClose
}: {
  config: ShopConfig;
  onClose: () => void;
}) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  return <div className="fixed inset-0 z-[60] bg-dark-navy/60 backdrop-blur-sm flex flex-col" data-id="element-892" data-mp="ec687sqxx11q">
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white" data-id="element-893" data-mp="3vso1kyyxi6u3">
        <p className="text-sm font-semibold text-dark-navy truncate" data-id="element-894" data-mp="4mux60khtmb7">
          talab.app/shop/{config.handle}
        </p>
        <div className="inline-flex p-1 bg-slate-100 rounded-xl" data-id="element-895" data-mp="sc75yzbwrscz">
          <button onClick={() => setDevice('desktop')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${device === 'desktop' ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500'}`} data-id="element-896" data-mp="x3fiw5cg5vuw">
            <MonitorIcon className="w-3.5 h-3.5" data-id="element-897" data-mp="2jgiv7ip6m425" />
            Desktop
          </button>
          <button onClick={() => setDevice('mobile')} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-150 ease-out ${device === 'mobile' ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500'}`} data-id="element-898" data-mp="2ffp7apbqop99">
            <SmartphoneIcon className="w-3.5 h-3.5" data-id="element-899" data-mp="334rnrtun0c26" />
            Mobile
          </button>
        </div>
        <button onClick={onClose} aria-label="Close preview" className="p-2 rounded-lg text-slate-400 hover:text-dark-navy hover:bg-slate-50 transition-colors duration-150 ease-out" data-id="element-900" data-mp="2y6gaf954vjf7">
          <XIcon className="w-5 h-5" data-id="element-901" data-mp="1mpq9o5jhbjsz" />
        </button>
      </div>
      <div className="flex-1 min-h-0 bg-slate-100" data-id="element-902" data-mp="28zbuhyhyuzx">
        <StorefrontViewport config={config} device={device} zoom="fit" className="w-full h-full" data-id="element-903" data-mp="2aaak3d35ck6" />
      </div>
    </div>;
}